import { useNavigate } from "@tanstack/react-router";
import { Check, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "@/lib/toast";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCreateConcept } from "@/hooks/useConcepts";
import { isThinDescription } from "@/lib/concept-heuristics";
import { RESEARCH_QUESTION_SENTENCES } from "@/lib/conceptGuidance";
import { useQuickStartStore } from "@/stores/useQuickStartStore";
import { ConceptRefinementPanel } from "./ConceptRefinementPanel";
import type { ConceptSuggestion } from "@/types/concept";

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  abstract: z.string().optional(),
  keywords: z.string().optional(),
});

type DialogState = "form" | "refining";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CreateConceptDialog({ open, onClose }: Props) {
  const [name, setName] = useState("");
  const [abstract, setAbstract] = useState("");
  const [keywords, setKeywords] = useState("");
  const [error, setError] = useState("");
  const [dialogState, setDialogState] = useState<DialogState>("form");
  const [confirmed, setConfirmed] = useState(false);
  const createConcept = useCreateConcept();
  const navigate = useNavigate();

  function reset() {
    setName("");
    setAbstract("");
    setKeywords("");
    setError("");
    setDialogState("form");
    setConfirmed(false);
  }

  function handleSuggestionSelect(suggestion: ConceptSuggestion) {
    setName(suggestion.name || name);
    if (suggestion.abstract) setAbstract(suggestion.abstract);
    if (suggestion.keywords.length) setKeywords(suggestion.keywords.join(", "));
    setConfirmed(true);
    setDialogState("form");
  }

  async function doCreate() {
    try {
      const concept = await createConcept.mutateAsync({
        name,
        abstract: abstract || undefined,
        keywords: keywords || undefined,
        auto_draft: true,
      });
      reset();
      onClose();
      toast.success("Concept created", { description: name });
      useQuickStartStore.getState().setConceptJustCreated(true);
      navigate({
        to: "/concepts/$conceptId/curate",
        params: { conceptId: concept.id },
      });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 403) {
        setError("You've reached your plan's concept limit.");
        toast.error("Concept limit reached");
      } else {
        setError("Failed to create concept. It may already exist.");
        toast.error("Failed to create concept");
      }
    }
  }

  async function handleSubmit() {
    const result = schema.safeParse({ name, abstract, keywords });
    if (!result.success) {
      setError(result.error.issues[0].message);
      return;
    }

    await doCreate();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Create Concept</DialogTitle>
          {dialogState === "form" ? (
            <DialogDescription asChild>
              <div className="space-y-2 pt-1 text-xs leading-relaxed text-muted-foreground">
                <p>
                  Your research question is what PaperChase grades every paper
                  against. Aim for a scoped question rather than a broad topic.
                  Leave it short and PaperChase will draft one for you to check.
                </p>
                <p>
                  <code className="rounded-sm bg-blue-500/10 px-1.5 py-0.5 font-mono text-[11px] text-foreground">
                    "Machine learning"
                  </code>{" "}
                  will surface ten thousand papers;{" "}
                  <code className="rounded-sm bg-blue-500/10 px-1.5 py-0.5 font-mono text-[11px] text-foreground">
                    "how does dropout regularization affect convergence speed in transformers"
                  </code>{" "}
                  will surface forty useful ones.
                </p>
                <p>
                  Not sure how to phrase it? Try{" "}
                  <strong className="font-medium text-foreground">
                    Help me refine
                  </strong>{" "}
                  below.
                </p>
              </div>
            </DialogDescription>
          ) : (
            <DialogDescription className="sr-only">
              Refining your concept to make it more specific.
            </DialogDescription>
          )}
        </DialogHeader>

        {dialogState === "refining" ? (
          <ConceptRefinementPanel
            initialName={name}
            initialDescription={abstract}
            initialKeywords={keywords}
            onSelect={handleSuggestionSelect}
            onBack={() => setDialogState("form")}
            onCreateAnyway={() => {
              doCreate();
            }}
          />
        ) : (
          <>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setConfirmed(false);
                  }}
                  placeholder="e.g. Machine Learning in Healthcare"
                  className={confirmed ? "border-green-600" : ""}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="abstract">Research question</Label>
                <p className="text-xs text-muted-foreground">PaperChase grades every paper against this.</p>
                <Textarea
                  id="abstract"
                  value={abstract}
                  onChange={(e) => {
                    setAbstract(e.target.value);
                    setConfirmed(false);
                  }}
                  placeholder={`${RESEARCH_QUESTION_SENTENCES} sentences: what are you trying to find out?`}
                  rows={3}
                  className={confirmed ? "border-green-600" : ""}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="keywords">Search terms</Label>
                <p className="text-xs text-muted-foreground">Used to find starting papers (seeds).</p>
                <Input
                  id="keywords"
                  value={keywords}
                  onChange={(e) => {
                    setKeywords(e.target.value);
                    setConfirmed(false);
                  }}
                  placeholder="e.g. deep learning, clinical trials"
                  className={confirmed ? "border-green-600" : ""}
                />
              </div>

              {/* Confirmed-state nudge — shown after Help me refine fires */}
              {confirmed && (
                <div className="flex items-start gap-2 rounded-md border-l-2 border-green-600 bg-green-950/20 px-3 py-2">
                  <Check className="mt-0.5 size-3 shrink-0 text-green-500" />
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    This concept is specific enough for accurate scoring. You can
                    edit any field before creating.
                  </p>
                </div>
              )}

              {/* Help me refine button */}
              <button
                type="button"
                onClick={() => setDialogState("refining")}
                className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-xs text-primary transition-colors hover:border-primary/50 hover:bg-muted/50"
              >
                <Sparkles className="size-3.5" />
                Help me refine this concept
              </button>

              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={createConcept.isPending}
                className={confirmed ? "bg-green-600 hover:bg-green-700" : ""}
              >
                {createConcept.isPending ? (
                  isThinDescription(abstract) ? "Drafting your research question…" : "Creating..."
                ) : confirmed ? (
                  <>
                    Create <Check className="ml-1 size-3.5" />
                  </>
                ) : (
                  "Create"
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
